import type { LayerState } from '@vitals/shared';
import type { VitalDef, VitalView } from './vitals.js';

/**
 * Weather vital selector.
 * Reads `summary.{ tempF, label }` from the Open-Meteo LayerState.
 * Returns `down: true` with a '—' value when the feed is unavailable.
 */
export const weatherVital: VitalDef = {
  id: 'weather',
  layerId: 'weather',
  select: (s: LayerState | undefined): VitalView => {
    const isDown =
      !s ||
      s.status === 'down' ||
      s.summary == null;

    if (isDown) {
      return {
        value: '—',
        label: 'weather',
        down: true,
        stale: false,
      };
    }

    const summary = s.summary as { tempF: number; label: string; precipIn?: number; windMph?: number };
    const tempF = Math.round(summary.tempF);

    return {
      value: `${tempF}°F`,
      label: summary.label,
      down: false,
      stale: s.status === 'stale',
      detailTitle: 'Current conditions',
      details: [
        { kind: 'metric', label: 'Conditions', value: `${summary.label.charAt(0).toUpperCase()}${summary.label.slice(1)}` },
        { kind: 'metric', label: 'Temperature', value: `${tempF}°F` },
        ...(summary.precipIn !== undefined
          ? [{ kind: 'metric' as const, label: 'Precipitation', value: `${summary.precipIn} in` }]
          : []),
        ...(summary.windMph !== undefined
          ? [{ kind: 'metric' as const, label: 'Wind', value: `${Math.round(summary.windMph)} mph` }]
          : []),
      ],
    };
  },
};
